import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import whatsappLogo  from '../../assets/whatsapp_logo.webp';
import telegramLogo  from '../../assets/telegramlogo.png';

/* ─────────────────────────────────────────────────────────────
   Logos a COLOR por defecto. `filter` solo para casos especiales
   (AWS: su logo original es oscuro → lo pasamos a blanco para que
   se vea sobre el fondo oscuro).
   ───────────────────────────────────────────────────────────── */
interface TechItem {
  name: string;
  src: string;
  filter?: string;
  hideName?: boolean;   // logos que ya son wordmark (AWS) no llevan etiqueta extra
}

// Marcas SOLO-ÍCONO (sin wordmark) para que el nombre no se duplique con el logo.
// AWS y Meta usaban wordmarks que repetían el texto del label → ahora van con su símbolo.
const TECH: TechItem[] = [
  { name: 'WhatsApp',        src: whatsappLogo },
  { name: 'Telegram',        src: telegramLogo },
  { name: 'Gmail',           src: 'https://upload.wikimedia.org/wikipedia/commons/7/7e/Gmail_icon_%282020%29.svg' },
  { name: 'Google Calendar', src: 'https://upload.wikimedia.org/wikipedia/commons/a/a5/Google_Calendar_icon_%282020%29.svg' },
  { name: 'Google Meet',     src: 'https://upload.wikimedia.org/wikipedia/commons/9/9b/Google_Meet_icon_%282020%29.svg' },
  { name: 'Cloudflare',      src: 'https://cdn.simpleicons.org/cloudflare/F38020' },
  { name: 'AWS',             src: 'https://upload.wikimedia.org/wikipedia/commons/9/93/Amazon_Web_Services_Logo.svg', filter: 'brightness(0) invert(1)', hideName: true },
  { name: 'Anthropic',       src: 'https://cdn.simpleicons.org/anthropic/D97757' },
  { name: 'Meta',            src: 'https://cdn.simpleicons.org/meta/0467DF' },
];

// 4 copias → loop visualmente perfecto en cualquier ancho de pantalla
const TRACK = [...TECH, ...TECH, ...TECH, ...TECH];

/* Separador decorativo entre items */
const Divider = () => (
  <span aria-hidden style={{
    display: 'inline-block', width: 4, height: 4, borderRadius: '50%',
    background: 'rgba(255,255,255,0.14)', flexShrink: 0, alignSelf: 'center',
  }} />
);

/* Item individual del carrusel */
const LogoItem: React.FC<{ item: TechItem }> = ({ item }) => (
  <div className="wct-item">
    <img
      src={item.src}
      alt={item.name}
      draggable={false}
      style={{
        height: item.hideName ? 24 : 32, width: 'auto', maxWidth: item.hideName ? 72 : 56,
        objectFit: 'contain', display: 'block', flexShrink: 0,
        filter: item.filter,
      }}
    />
    {!item.hideName && <span className="wct-name">{item.name}</span>}
  </div>
);

const WorldClassTech: React.FC = () => {
  const { t } = useTranslation();
  return (
  <section
    id="integraciones"
    style={{ position: 'relative', padding: 'clamp(3.5rem, 7vh, 5.5rem) 0', overflow: 'hidden' }}
  >
    <style>{`
      @keyframes wct-scroll {
        from { transform: translateX(0); }
        to   { transform: translateX(-25%); }
      }
      .wct-track  { animation: wct-scroll 38s linear infinite; }
      .wct-wrap:hover .wct-track { animation-play-state: paused; }

      .wct-item {
        display: inline-flex; align-items: center; gap: 11px;
        flex-shrink: 0; cursor: default; padding: 0.2rem 0;
        transition: transform 0.3s cubic-bezier(0.16,1,0.3,1);
      }
      .wct-item img { transition: filter 0.3s ease; }
      .wct-item:hover { transform: scale(1.08); }

      .wct-name {
        font-family: var(--font-heading); font-weight: 600;
        font-size: 0.85rem; white-space: nowrap;
        color: rgba(255,255,255,0.55);
        letter-spacing: 0.03em;
        transition: color 0.3s ease;
        user-select: none;
      }
      .wct-item:hover .wct-name { color: rgba(255,255,255,0.95); }
    `}</style>

    {/* ── Encabezado — mismo formato que el resto de la página ── */}
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      style={{ textAlign: 'center', marginBottom: 'clamp(1.8rem, 3.5vw, 2.8rem)', padding: '0 1.5rem', position: 'relative', zIndex: 1 }}
    >
      <motion.h2
        initial={{ opacity: 0, y: 14 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.08, duration: 0.6 }}
        style={{
          fontFamily: 'var(--font-heading)', fontWeight: 300,
          fontSize: 'clamp(2rem, 4vw, 3.5rem)',
          color: 'var(--text-primary)', margin: 0,
          lineHeight: 1.12, letterSpacing: '-0.03em',
        }}
      >
        {t('worldclasstech.title1', 'Construido sobre plataformas')}{' '}
        <span className="grad-text" style={{ fontWeight: 800 }}>{t('worldclasstech.title2', 'que ya conoces.')}</span>
      </motion.h2>
    </motion.div>

    {/* ── Carrusel ── */}
    <motion.div
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8, delay: 0.1 }}
    >
      <div
        className="wct-wrap"
        style={{
          overflow: 'hidden',
          WebkitMaskImage: 'linear-gradient(to right, transparent 0%, #000 14%, #000 86%, transparent 100%)',
          maskImage:       'linear-gradient(to right, transparent 0%, #000 14%, #000 86%, transparent 100%)',
        }}
      >
        <div
          className="wct-track"
          style={{ display: 'inline-flex', alignItems: 'center', gap: '2.4rem' }}
        >
          {TRACK.map((item, i) => (
            <React.Fragment key={i}>
              <LogoItem item={item} />
              {(i + 1) % TECH.length !== 0 && <Divider />}
            </React.Fragment>
          ))}
        </div>
      </div>
    </motion.div>
  </section>
  );
};

export default WorldClassTech;
